import { isOnWeb, type AssetManifest } from "./content";
import { arLayerModelOf, arLayerOf, arModelKey, arTargetKey } from "./arKeys";
import { normalizeArInteract } from "./arInteract";

/**
 * ✨ **어느 쪽에 마법이 있나** — 판정을 한 곳에.
 *
 * ## 🔴 왜 필요한가 (2026-09-10)
 * 마법은 **들어가 봐야만** 있는지 알 수 있었다. 세 권 62쪽 가운데 마법이 있는 쪽은 11쪽인데,
 * 아이도 보호자도 **어느 쪽인지 알 길이 없었다** — 한 쪽씩 넘겨 보다 못 찾으면 그만이다.
 * 「만들어 놓고 닿을 길이 없다」는 이 저장소가 이미 세 번 겪은 사고다.
 *
 * ## 🔴 「정의가 있다」와 「아이에게 보인다」는 다르다
 * 정의만 있고 층 그림이 하나도 안 나가면 「✨ 마법 보기」는 떠도 **눌러야 아무것도 없다.**
 * 그래서 세는 규칙은 **책 화면이 켜는 규칙과 같아야 한다**(`bookData` 가 이 함수를 쓴다).
 * 화면마다 다시 짜면 「목록엔 3쪽이라는데 그 쪽엔 단추가 없다」가 된다.
 */

/** 책 화면이 아는 쪽의 모양 — 마법을 켤지 정하는 데 필요한 것만. */
export interface MagicPageLike {
  magic?: unknown;
  magicLayers?: Record<string, unknown>;
  /** 🧊 층별 3D 파일 — 그림이 없어도 3D 가 있으면 아이가 볼 것이 있다 */
  magicModels?: Record<string, unknown>;
}

/**
 * 이 쪽에 **아이가 쓸 수 있는** 마법이 있나.
 * 정의(`magic`)에 더해 나가는 **층 그림이나 3D 파일**이 하나라도 있어야 한다.
 *
 * 🔴 2026-09-14 · 전에는 그림만 셌다. 운영자가 [🧊 3D 파일]만 올리면 관리자 미리보기엔 3D 가 서는데
 *    아이 화면(책·카메라)엔 마법이 **아예 안 열렸다** — 「관리자에선 되는데」의 그 모양이다.
 */
export function pageHasMagic(p: MagicPageLike | undefined): boolean {
  const n = (r: Record<string, unknown> | undefined) => (r ? Object.keys(r).length : 0);
  return !!p?.magic && (n(p.magicLayers) > 0 || n(p.magicModels) > 0);
}

/** 마법이 있는 **쪽 번호**(1부터 — 아이가 화면에서 보는 번호와 같다). */
export function magicPageNos(pages: readonly MagicPageLike[]): number[] {
  const out: number[] = [];
  pages.forEach((p, i) => {
    if (pageHasMagic(p)) out.push(i + 1);
  });
  return out;
}

/**
 * 이 장면의 **나가는 층 자리 이름들**(`ar.layer.img.<층>.<장면>`).
 * 「자리가 있다」가 아니라 「주소가 있고 안 숨겼다」(`isOnWeb`)여야 센다.
 */
export function magicLayerKeys(manifest: AssetManifest, sceneId: string): string[] {
  return Object.keys(manifest.assets).filter((k) => {
    const l = arLayerOf(k);
    return !!l && l.sceneId === sceneId && isOnWeb(manifest.assets[k]);
  });
}

/**
 * 🧊 이 장면의 **나가는 3D 파일 자리 이름들**(`ar.layer.model.<층>.<장면>`).
 * 주소가 있고 안 숨겼고(`isOnWeb`) **`.glb`** 인 것만(불변식 7 — 화면이 3D 로 띄울 수 있는 것만 센다).
 */
export function magicModelKeys(manifest: AssetManifest, sceneId: string): string[] {
  return Object.keys(manifest.assets).filter((k) => {
    const m = arLayerModelOf(k);
    const e = manifest.assets[k];
    return !!m && m.sceneId === sceneId && isOnWeb(e) && /\.glb(\?|$)/i.test(e?.url ?? "");
  });
}

/**
 * 🧊 **이 장면(쪽·카드)에 3D 가 붙었나** — 마법 층의 3D(`ar.layer.model.*`) 또는 AR 보기 ④ 3D 모델(`ar.model.<장면>`).
 * 관리자 카드 줄의 「🧊 3D」 표시가 이걸 부른다(2026-10-03 — 「3D 칸이 메뉴에 안 보여」: 어디에 3D 가 있는지 줄에서 안 보였다).
 * 숨긴 것·`.glb` 가 아닌 것은 안 센다(magicModelKeys 와 같은 잣대).
 */
export function sceneHas3d(manifest: AssetManifest, sceneId: string): boolean {
  if (magicModelKeys(manifest, sceneId).length > 0) return true;
  const e = manifest.assets[arModelKey(sceneId)];
  return isOnWeb(e) && /\.glb(\?|$)/i.test(e?.url ?? "");
}

/**
 * 지금 **아이에게 진짜 보이는** 마법 장면들(`<책>.<쪽>`).
 * 책을 안 열고도 셀 수 있다 — 책 고르는 화면이 이걸로 「✨ 마법 2쪽」을 적는다.
 *
 * 🔴 책에서 지운 쪽의 장면이 매니페스트에 남아 있으면 여기 낀다. 그건 `pnpm check:magic`
 *    이 잡는 다른 문제다 — 여기서 책 문서까지 읽으면 고르는 화면이 책 세 권을 다 받아야 한다.
 */
export function liveMagicScenes(manifest: AssetManifest): string[] {
  const out: string[] = [];
  for (const k of Object.keys(manifest.assets)) {
    const sceneId = k.startsWith("ar.target.") ? k.slice("ar.target.".length) : null;
    if (!sceneId || !sceneId.includes(".")) continue; // `<책>.<쪽>` 인 것만(경주 카드는 책 쪽이 아니다)
    if (!normalizeArInteract(manifest.assets[k]?.interact)) continue;
    // 그림도 3D 도 하나 없으면 아이가 볼 것이 없다(pageHasMagic 과 같은 규칙)
    if (magicLayerKeys(manifest, sceneId).length === 0 && magicModelKeys(manifest, sceneId).length === 0) continue;
    out.push(sceneId);
  }
  return out;
}

/** 책별 마법 쪽 수 — `{ tree: 2, poljjak: 7 }`. */
export function magicSceneCountByBook(manifest: AssetManifest): Record<string, number> {
  const out: Record<string, number> = {};
  for (const id of liveMagicScenes(manifest)) {
    const book = id.slice(0, id.indexOf("."));
    out[book] = (out[book] ?? 0) + 1;
  }
  return out;
}

/**
 * 아이·보호자에게 하는 말. **없으면 아무 말도 안 한다**(빈 문자열) —
 * 「마법 0쪽」은 알려 주는 게 아니라 없는 것을 광고하는 것이다.
 */
export function magicPagesSay(nos: readonly number[]): string {
  if (nos.length === 0) return "";
  return `${nos.join("·")}쪽에 마법이 있어요`;
}

/** 자리 이름 만들기는 shared 한 곳(`arTargetKey`) — 여기서 이어 붙이지 않는다. */
export function magicTargetKey(bookId: string, pageId: string): string {
  return arTargetKey(`${bookId}.${pageId}`);
}
